// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// --- RxJs
import { retryBackoff } from 'backoff-rxjs';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

// --- HS Code
import { environment } from '../../../../environments/environment';
import {
  TokenValidationErrorTypes,
  ApplicationError,
  ImpersonationTokenValidationModel,
  ImpersonationValidationStages,
  IApplicationError,
  ErrorMessage,
} from '../../../models';
import { ImpersonationValidationBaseService } from './impersonation-validation-base.service';
import { OidcService } from '../account/oidc.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export class ImpersonationValidationService extends ImpersonationValidationBaseService {
  constructor(
    private http: HttpClient,
    oidcService: OidcService
  ) {
    super(oidcService);
  }

  validateImpersonationToken(token: string): void {
    if (!token) {
      return;
    }

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.impersonationValidationApi +
      `/${token}`;

    this.http
      .get<ImpersonationTokenValidationModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._validationError.bind(this))
      )
      .subscribe((result) => {
        if (
          result.tokenValidationErrorType === TokenValidationErrorTypes.noError
        ) {
          this._validationStage$.next(
            ImpersonationValidationStages.authenticating
          );
          this.oidcService.signInRedirect({
            extraQueryParams: {
              acr_values: 'otac:' + result.otac,
            },
          });
          return;
        }
        this._evaluateError(result);
      });
  }

  private _evaluateError(result: ImpersonationTokenValidationModel): void {

    let errorCode = ErrorMessage.impersonationValidationInvalidTokenErrorCode;

    this._validationStage$.next(ImpersonationValidationStages.tokenError);
    switch (result.tokenValidationErrorType) {
      case TokenValidationErrorTypes.invalidToken:
        break;
      case TokenValidationErrorTypes.tokenIsUsed:
        errorCode = ErrorMessage.impersonationValidationUsedTokenErrorCode;
        break;
      case TokenValidationErrorTypes.tokenIsExpired:
        errorCode = ErrorMessage.impersonationValidationExpiredTokenErrorCode;
        break;
      default:
        this._unexpectedError();
        break;
    }

    result.applicationError = ApplicationError.create(
      errorCode,
      'impersonationValidationService',
      false
    );
    result.applicationError.errorHeader = 'errorHeaderTokenError';

    this._validation$.next(result);
  }

  private _unexpectedError() {
    this._validationStage$.next(ImpersonationValidationStages.tokenError);
    this._validation$.next({
      tokenValue: '',
      hasPassword: false,
      tokenValidationErrorType: TokenValidationErrorTypes.unexpectedError,
      locale: '',
      applicationError: {
        errorMessage: 'errorHeaderUnexpected',
        detailedMessage: 'impersonationValidationUnexpectedError',
      },
    } as ImpersonationTokenValidationModel);
  }

  private _validationError(err: any) {
    return of({
      tokenValue: '',
      hasPassword: false,
      tokenValidationErrorType: TokenValidationErrorTypes.unexpectedError,
      locale: '',
      applicationError: {
        errorMessage: 'errorHeaderUnexpected',
        detailedMessage: 'impersonationValidationUnexpectedError',
      },
    } as ImpersonationTokenValidationModel);
  }
}
